import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  CircleCheck,
  CreditCard,
  LayoutDashboard,
  MessageCircle,
  PackageCheck,
  QrCode,
  Send,
  Sparkles,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

/* =========================================================
   STATIC DATA
========================================================= */

const TYPEWRITER_TEXT = "Build Your Referral Network.";

const heroTasks = [
  {
    number: "1",
    title: "Buy Software (₹1,000)",
    description: "Instant license delivery & cloud proxy",
    badge: "₹1,000 License",
    icon: PackageCheck,
    type: "green",
  },
  {
    number: "2",
    title: "Complete Assigned Tasks",
    description: "Run broadcasts, templates & bot flows",
    badge: "Active",
    icon: Send,
    type: "blue",
  },
  {
    number: "3",
    title: "Refer Members & Earn ₹200",
    description: "Credited upon verified software purchase",
    badge: "+₹200",
    icon: Users,
    type: "purple",
  },
  {
    number: "4",
    title: "Withdraw via UPI or QR",
    description: "Disbursed with genuine banking UTR",
    badge: "Fast",
    icon: QrCode,
    type: "green",
  },
];

const workflowSteps = [
  {
    number: "01",
    title: "Buy Software",
    description:
      "₹1,000 one-time purchase with instant cloud access.",
    icon: PackageCheck,
  },
  {
    number: "02",
    title: "Complete Tasks",
    description:
      "Execute assigned automated workflows & broadcasts.",
    icon: Zap,
  },
  {
    number: "03",
    title: "Refer Members",
    description:
      "Share your link. Peer purchases software for ₹1,000.",
    icon: Users,
  },
  {
    number: "04",
    title: "Wallet Credited",
    description:
      "Receive verified ₹200 commission per eligible purchase.",
    icon: Wallet,
  },
  {
    number: "05",
    title: "UPI Withdrawal",
    description:
      "Direct payout to your UPI ID or QR code with banking UTR.",
    icon: CreditCard,
  },
];

const referralOptions = [1, 5, 10, 25, 50, 100];

/* =========================================================
   HOME
========================================================= */

function Home() {
  const [typedText, setTypedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [referrals, setReferrals] = useState(1);

  /* =======================================================
     TYPEWRITER
  ======================================================= */

  useEffect(() => {
    const typingSpeed = isDeleting ? 55 : 90;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        setTypedText(
          TYPEWRITER_TEXT.substring(
            0,
            typedText.length + 1
          )
        );

        if (typedText === TYPEWRITER_TEXT) {
          setTimeout(() => {
            setIsDeleting(true);
          }, 1800);
        }
      } else {
        setTypedText(
          TYPEWRITER_TEXT.substring(
            0,
            typedText.length - 1
          )
        );

        if (typedText === "") {
          setIsDeleting(false);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [typedText, isDeleting]);

  /* =======================================================
     SCROLL REVEAL
  ======================================================= */

  useEffect(() => {
    const elements =
      document.querySelectorAll(".scroll-reveal");

    if (!elements.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
      }
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  /* =======================================================
     CALCULATOR
  ======================================================= */

  const commission = referrals * 200;

  const money = (value) =>
    new Intl.NumberFormat("en-IN").format(value);

  return (
    <main className="min-h-screen overflow-x-hidden bg-white text-slate-900">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section
        id="home"
        className="hero-section relative overflow-hidden border-b border-slate-200"
      >
        {/* Background */}

        <div className="hero-grid absolute inset-0 opacity-40" />

        <div className="hero-blob hero-blob-one" />
        <div className="hero-blob hero-blob-two" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 sm:py-20 lg:grid-cols-[1fr_.92fr] lg:gap-16 lg:px-8 lg:py-24">

          {/* =================================================
              HERO LEFT
          ================================================= */}

          <div className="hero-copy max-w-2xl">

            {/* Badge */}

            <div className="hero-item mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 shadow-sm">

              <Sparkles
                size={13}
                className="text-emerald-500"
              />

              <span className="text-[9px] font-black uppercase tracking-[0.12em] text-emerald-700 sm:text-[10px]">
                WhatsApp Cloud Suite & Verified Referral Engine
              </span>
            </div>

            {/* Heading */}

            <h1 className="hero-item text-[40px] font-black leading-[1.08] tracking-[-0.045em] text-[#071a3b] sm:text-5xl lg:text-[57px]">

              Automate WhatsApp.

              <br />

              <span className="text-[#00a96d]">
                Complete Tasks.
              </span>

              <br />

              <span className="relative inline-block min-h-[1.1em] text-[#071a3b]">
                {typedText}

                <span className="type-cursor">
                  |
                </span>
              </span>
            </h1>

            {/* Description */}

            <p className="hero-item mt-6 max-w-xl text-[14px] leading-7 text-slate-500 sm:mt-7 sm:text-base">
              Get access to our WhatsApp Automation Software,
              complete assigned tasks, refer eligible users, and
              track your earnings from one professional dashboard.
            </p>

            {/* Pricing */}

            <div className="hero-item mt-7 grid max-w-xl gap-3 sm:mt-8 sm:grid-cols-2">

              {/* Software */}

              <div className="price-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm">

                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
                  Software Access
                </div>

                <div className="text-xl font-black text-slate-800">
                  ₹1,000

                  <span className="ml-1.5 text-[10px] font-semibold text-slate-400">
                    Monthly Subscription
                  </span>
                </div>
              </div>

              {/* Commission */}

              <div className="price-card rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 shadow-sm">

                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-emerald-600">
                  Referral Commission
                </div>

                <div className="text-xl font-black text-emerald-700">
                  Earn ₹200

                  <span className="ml-1.5 text-[10px] font-semibold text-emerald-600">
                    / Purchase
                  </span>
                </div>
              </div>
            </div>

            {/* CTA */}

            <div className="hero-item mt-7 flex flex-wrap items-center gap-3">

              <Link
                to="/signup"
                className="hero-primary group flex items-center rounded-xl bg-[#00a96d] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/20"
              >
                Get Started

                <ArrowRight
                  size={16}
                  className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>

              <Link
                to="/login"
                className="hero-secondary rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 shadow-sm"
              >
                Login
              </Link>
            </div>

            {/* Disclaimer */}

            <div className="hero-item mt-5 flex max-w-xl gap-2 text-[10px] leading-5 text-slate-400 sm:mt-6">

              <CircleCheck
                size={14}
                className="mt-0.5 shrink-0 text-emerald-500"
              />

              <span>
                Transparent performance-based earnings. Strictly
                ₹200 per verified software purchase. No fixed salary
                promises.
              </span>
            </div>
          </div>

          {/* =================================================
              HERO RIGHT
          ================================================= */}

          <div className="dashboard-area relative mx-auto w-full max-w-[540px]">

            {/* Glow */}

            <div className="dashboard-glow" />

            {/* Floating Commission */}

            <div className="floating-commission absolute -left-5 top-12 z-20 hidden rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl sm:block">

              <div className="flex items-center gap-2">

                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
                  <Wallet
                    size={15}
                    className="text-emerald-600"
                  />
                </div>

                <div>
                  <div className="text-[8px] font-bold text-slate-400">
                    Referral Commission
                  </div>

                  <div className="text-sm font-black text-emerald-600">
                    +₹200
                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                MAIN DASHBOARD CARD
            ================================================= */}

            <div className="dashboard-card relative z-10 rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_30px_80px_-30px_rgba(15,23,42,.35)] sm:p-6">

              {/* Header */}

              <div className="flex items-center justify-between border-b border-slate-100 pb-5">

                <div className="flex min-w-0 items-center gap-3">

                  <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#05ad72] shadow-sm">

                    <MessageCircle
                      size={21}
                      className="text-white"
                    />

                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-400" />
                  </div>

                  <div className="min-w-0">

                    <div className="truncate text-sm font-black text-slate-800">
                      AutoFlow WA Engine
                    </div>

                    <div className="mt-0.5 flex items-center gap-1.5 text-[9px] font-bold text-emerald-600">

                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />

                      Live System Demo
                    </div>
                  </div>
                </div>

                <div className="ml-3 shrink-0 rounded-lg bg-slate-100 px-3 py-2 text-[9px] font-black text-slate-600 sm:text-[11px]">
                  ₹1,000 License
                </div>
              </div>

              {/* Task List */}

              <div className="mt-4 space-y-3">

                {heroTasks.map((task, index) => {
                  const TaskIcon = task.icon;

                  return (
                    <div
                      key={task.number}
                      className="hero-task group flex items-center gap-3 rounded-xl border border-slate-100 bg-[#f8fafc] p-3.5"
                      style={{
                        animationDelay: `${index * 130}ms`,
                      }}
                    >

                      {/* Icon */}

                      <div
                        className={`task-number flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                          task.type === "green"
                            ? "bg-emerald-100 text-emerald-600"
                            : task.type === "blue"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-purple-100 text-purple-600"
                        }`}
                      >
                        <TaskIcon
                          size={17}
                          strokeWidth={2}
                        />
                      </div>

                      {/* Content */}

                      <div className="min-w-0 flex-1">

                        <div className="truncate text-[11px] font-bold text-slate-800 sm:text-xs">
                          {task.title}
                        </div>

                        <div className="mt-0.5 truncate text-[9px] text-slate-400 sm:text-[10px]">
                          {task.description}
                        </div>
                      </div>

                      {/* Status */}

                      <div
                        className={`flex shrink-0 items-center gap-1 text-[9px] font-bold sm:text-[10px] ${
                          task.type === "green"
                            ? "text-emerald-600"
                            : task.type === "blue"
                            ? "text-blue-600"
                            : "text-purple-600"
                        }`}
                      >
                        {index === 0 && (
                          <Check
                            size={14}
                            strokeWidth={2.5}
                          />
                        )}

                        {index === 3 && (
                          <Zap
                            size={13}
                            fill="currentColor"
                          />
                        )}

                        {task.badge}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Divider */}

              <div className="my-5 h-px bg-slate-100" />

              {/* Dashboard Link */}

              <div className="flex items-center justify-between">

                <span className="text-xs font-medium text-slate-500">
                  Quick Experience:
                </span>

                <Link
                  to="/dashboard"
                  className="group flex items-center text-xs font-black text-emerald-600"
                >
                  Open Dashboard

                  <ChevronRight
                    size={16}
                    className="ml-0.5 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>

          </div>
        </div>

        {/* =================================================
            QUICK EXPERIENCE BAR
        ================================================= */}

        <div className="border-t border-slate-100 bg-white/80">

          <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 lg:px-8">

            <div className="flex items-center gap-2 text-xs font-bold text-slate-500">

              <LayoutDashboard
                size={15}
                className="text-emerald-500"
              />

              Quick Experience
            </div>

            <Link
              to="/dashboard"
              className="group flex items-center text-xs font-black text-emerald-600"
            >
              Open Dashboard

              <ArrowRight
                size={14}
                className="ml-1.5 transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* =====================================================
          SYSTEM WORKFLOW
      ===================================================== */}

      <section
        id="workflow"
        className="relative overflow-hidden bg-[#f7fafc] px-5 py-20 sm:py-24 lg:px-8"
      >

        <div className="workflow-decoration workflow-decoration-one" />
        <div className="workflow-decoration workflow-decoration-two" />

        <div className="relative mx-auto max-w-6xl">

          {/* Heading */}

          <div className="scroll-reveal mx-auto max-w-3xl text-center">

            <div className="section-label">
              System Workflow
            </div>

            <h2 className="mt-5 text-3xl font-black leading-tight tracking-[-0.04em] text-[#071a3b] sm:text-4xl lg:text-[44px]">
              WhatsApp Automation{" "}
              <span className="text-emerald-500">→</span>{" "}
              Tasks{" "}
              <span className="text-emerald-500">→</span>{" "}
              Referrals{" "}
              <span className="text-emerald-500">→</span>{" "}
              Earnings{" "}
              <span className="text-emerald-500">→</span>{" "}
              Withdrawal
            </h2>

            <p className="mx-auto mt-5 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
              A transparent, unified software workflow built for
              reliability and real performance tracking.
            </p>
          </div>

          {/* Workflow Cards */}

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:mt-14 lg:grid-cols-5">

            {workflowSteps.map((step, index) => {
              const WorkflowIcon = step.icon;

              return (
                <div
                  key={step.number}
                  className="workflow-card scroll-reveal group relative"
                  style={{
                    transitionDelay: `${index * 100}ms`,
                  }}
                >

                  {index !== workflowSteps.length - 1 && (
                    <div className="workflow-line hidden lg:block" />
                  )}

                  <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-500 group-hover:-translate-y-2 group-hover:border-emerald-200 group-hover:shadow-xl">

                    <div className="flex items-center justify-between">

                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition duration-300 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white">

                        <WorkflowIcon size={19} />
                      </div>

                      <span className="text-[9px] font-black uppercase tracking-wider text-slate-300">
                        Step {step.number}
                      </span>
                    </div>

                    <h3 className="mt-6 text-sm font-black text-slate-800 sm:text-base">
                      {step.title}
                    </h3>

                    <p className="mt-2 text-xs leading-5 text-slate-400">
                      {step.description}
                    </p>

                    <div className="mt-6 h-1 w-8 rounded-full bg-emerald-400 transition-all duration-500 group-hover:w-14" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          TRANSPARENT CALCULATOR
      ===================================================== */}

      <section
        id="calculator"
        className="bg-[#f7fafc] px-5 pb-20 sm:pb-24 lg:px-8"
      >

        <div className="mx-auto max-w-6xl">

          <div className="calculator-box scroll-reveal overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_25px_80px_-40px_rgba(15,23,42,.35)]">

            <div className="grid lg:grid-cols-[1.1fr_.9fr]">

              {/* LEFT */}

              <div className="p-7 sm:p-10 lg:p-12">

                <div className="calculator-label">
                  Transparent Calculator
                </div>

                <h2 className="mt-5 text-3xl font-black tracking-[-0.04em] text-[#071a3b] sm:text-4xl">
                  Calculate Your Referral Potential
                </h2>

                <p className="mt-5 max-w-xl text-sm leading-6 text-slate-500">
                  Every user who signs up through your link and
                  purchases the ₹1,000 WhatsApp Automation Software
                  generates a verified{" "}
                  <strong className="font-bold text-slate-700">
                    ₹200 referral commission
                  </strong>{" "}
                  credited straight to your wallet.
                </p>

                {/* Simulator */}

                <div className="mt-9">

                  <div className="flex items-center justify-between">

                    <span className="text-xs font-bold text-slate-700">
                      Simulate Referrals:
                    </span>

                    <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-600">
                      {referrals}{" "}
                      {referrals === 1
                        ? "Referral"
                        : "Referrals"}
                    </span>
                  </div>

                  {/* Range */}

                  <div className="mt-6">

                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={referrals}
                      aria-label="Number of referrals"
                      onChange={(event) =>
                        setReferrals(
                          Number(event.target.value)
                        )
                      }
                      style={{
                        "--range-progress": `${
                          ((referrals - 1) / 99) * 100
                        }%`,
                      }}
                      className="referral-slider w-full"
                    />

                    <div className="mt-3 flex justify-between text-[10px] font-semibold text-slate-400">
                      <span>1</span>
                      <span>25</span>
                      <span>50</span>
                      <span>75</span>
                      <span>100 Referrals</span>
                    </div>
                  </div>

                  {/* Presets */}

                  <div className="mt-7 flex flex-wrap gap-2">

                    {referralOptions.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setReferrals(value)
                        }
                        className={`rounded-lg border px-3 py-2 text-[10px] font-bold transition-all duration-200 ${
                          referrals === value
                            ? "border-emerald-400 bg-emerald-50 text-emerald-600 shadow-sm"
                            : "border-slate-200 bg-white text-slate-500 hover:border-emerald-300 hover:text-emerald-600"
                        }`}
                      >
                        {value} (₹
                        {money(value * 200)})
                      </button>
                    ))}
                  </div>
                </div>

                <Link
                  to="/signup"
                  className="hero-primary mt-9 inline-flex items-center rounded-xl bg-[#00a96d] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20"
                >
                  Create Account & Get Referral Link

                  <ArrowRight
                    size={15}
                    className="ml-2"
                  />
                </Link>
              </div>

              {/* RIGHT */}

              <div className="calculator-result flex items-center justify-center bg-[#101a35] p-8 sm:p-12">

                <div className="w-full text-center">

                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">
                    Estimated Referral Commission
                  </div>

                  <div
                    key={commission}
                    className="commission-number mt-5 text-5xl font-black tracking-[-0.05em] text-emerald-400 sm:text-6xl"
                  >
                    ₹{money(commission)}
                  </div>

                  <p className="mx-auto mt-5 max-w-xs text-xs leading-5 text-slate-400">
                    Based on{" "}
                    <strong className="text-white">
                      {referrals}
                    </strong>{" "}
                    verified software purchase
                    {referrals !== 1 ? "s" : ""} at ₹200
                    each.
                  </p>

                  <div className="mx-auto mt-5 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] text-slate-400">
                    Minimum withdrawal threshold is{" "}
                    <span className="ml-1 font-bold text-white">
                      ₹500
                    </span>
                  </div>

                  {/* Summary */}

                  <div className="mx-auto mt-8 max-w-xs rounded-xl border border-white/10 bg-white/5 p-4">

                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">
                        Verified Purchases
                      </span>

                      <span className="font-bold text-white">
                        {referrals}
                      </span>
                    </div>

                    <div className="my-3 h-px bg-white/10" />

                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">
                        Commission / Purchase
                      </span>

                      <span className="font-bold text-emerald-400">
                        ₹200
                      </span>
                    </div>

                    <div className="my-3 h-px bg-white/10" />

                    <div className="flex justify-between">
                      <span className="text-xs font-bold text-white">
                        Total
                      </span>

                      <span className="text-sm font-black text-emerald-400">
                        ₹{money(commission)}
                      </span>
                    </div>
                  </div>

                  <Link
                    to="/signup"
                    className="mt-7 inline-flex items-center rounded-xl bg-emerald-500 px-6 py-3 text-xs font-bold text-white transition duration-300 hover:-translate-y-1 hover:bg-emerald-400"
                  >
                    Create Account & Get Referral Link

                    <ArrowRight
                      size={14}
                      className="ml-2"
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;